/**
 * Sentry Machine ID Monitor
 * Monitors machineId and machineName state and reports to Sentry if they're empty or invalid
 * after the app has been running for a few seconds.
 *
 * This helps diagnose issues where performance mode or other factors may prevent
 * proper initialization of critical host identifiers.
 */

import * as Sentry from '@sentry/react';
import { getElectronStores } from '../stores/electronStores';
import { getSharedStores, performanceModeState } from '../stores/shared';

const FIRST_CHECK_DELAY_MS = 5000;
const RECHECK_DELAY_MS = 3000;
let monitorInitialized = false;

/**
 * Initialize the machine ID monitor.
 * Checks at 5s; if values are empty, requests a resync and re-checks at 8s.
 * Only reports to Sentry if the second check also finds empty values.
 */
export function initializeMachineIdMonitor(): void {
  if (monitorInitialized) {
    return;
  }

  monitorInitialized = true;

  console.log(`[MachineIdMonitor] Starting monitor - first check in ${FIRST_CHECK_DELAY_MS / 1000}s`);

  setTimeout(() => {
    const needsRecheck = checkMachineIdState(false);
    if (needsRecheck) {
      console.warn('[MachineIdMonitor] Values empty at first check, requesting resync and scheduling recheck');
      window.playcast?.requestResync?.();
      setTimeout(() => {
        checkMachineIdState(true);
      }, RECHECK_DELAY_MS);
    }
  }, FIRST_CHECK_DELAY_MS);
}

/**
 * Check the machine ID state.
 * @param reportToSentry - If true, report failures to Sentry. False on the first check
 *   so we can attempt a resync before reporting.
 * @returns true if values are invalid and a recheck is warranted.
 */
function checkMachineIdState(reportToSentry: boolean): boolean {
  const electronStores = getElectronStores();
  const sharedStores = getSharedStores();

  const machineId = electronStores.machineIdState.currentState;
  const machineName = electronStores.machineNameState.currentState;
  const hostEdition = sharedStores.hostState.currentState.edition;
  const adModeEnabled = sharedStores.adModeState.currentState.enabled;
  const performanceModeEnabled = performanceModeState.currentState.enabled;
  const nodeServiceVersion = sharedStores.servicePresenceState.currentState.version;
  const nodeServiceBranch = sharedStores.servicePresenceState.currentState.branch;

  const checkLabel = reportToSentry ? 'recheck' : 'first check';

  console.log(`[MachineIdMonitor] Checking state (${checkLabel}):`, {
    machineId: machineId || '(empty)',
    machineName: machineName || '(empty)',
    hostEdition: hostEdition || '(not set)',
    performanceMode: performanceModeEnabled,
    adMode: adModeEnabled,
    nodeServiceVersion: nodeServiceVersion || '(not set)',
    nodeServiceBranch: nodeServiceBranch || '(not set)',
    isValid: !!(machineId && machineName),
  });

  const isMachineIdInvalid = !machineId || machineId.trim() === '';
  const isMachineNameInvalid = !machineName || machineName.trim() === '';

  if (isMachineIdInvalid || isMachineNameInvalid) {
    const level: 'error' | 'warning' = (isMachineIdInvalid && isMachineNameInvalid) ? 'error' : 'warning';

    const errorMessage: string[] = [];
    if (isMachineIdInvalid) errorMessage.push('machineId is empty');
    if (isMachineNameInvalid) errorMessage.push('machineName is empty');

    console.error(`[MachineIdMonitor] ${level.toUpperCase()}: ${errorMessage.join(' and ')}`);

    if (reportToSentry) {
      Sentry.captureException(new Error(`Host initialization issue: ${errorMessage.join(' and ')}`), {
        level,
        tags: {
          issue_type: 'machine_id_initialization',
          machine_id_empty: isMachineIdInvalid ? 'true' : 'false',
          machine_name_empty: isMachineNameInvalid ? 'true' : 'false',
          host_edition: hostEdition || 'unknown',
          performance_mode: performanceModeEnabled ? 'true' : 'false',
          ad_mode: adModeEnabled ? 'true' : 'false',
          node_service_version: nodeServiceVersion || 'unknown',
          node_service_branch: nodeServiceBranch || 'unknown',
          resync_attempted: 'true',
        },
        contexts: {
          machine_state: {
            machineId: machineId || '(empty)',
            machineName: machineName || '(empty)',
            checkTimestamp: Date.now(),
            firstCheckDelayMs: FIRST_CHECK_DELAY_MS,
            recheckDelayMs: RECHECK_DELAY_MS,
          },
          environment: {
            isElectron: typeof window !== 'undefined' && window.playcast !== undefined,
            performanceMode: performanceModeEnabled,
            performanceModeLocalStorage: typeof window !== 'undefined' && localStorage.getItem('performanceMode'),
            guestAccount: typeof window !== 'undefined' && window.playcast?.guestAccount,
            steamInstall: typeof window !== 'undefined' && window.playcast?.steamInstall,
            hostEdition: hostEdition || '(not set)',
            adModeEnabled: adModeEnabled,
          },
          node_service: {
            version: nodeServiceVersion || '(not set)',
            branch: nodeServiceBranch || '(not set)',
          },
        },
        extra: {
          machineId: machineId || '(empty)',
          machineName: machineName || '(empty)',
          hostEdition: hostEdition || '(not set)',
          adModeEnabled: adModeEnabled,
          performanceModeEnabled: performanceModeEnabled,
          nodeServiceVersion: nodeServiceVersion || '(not set)',
          nodeServiceBranch: nodeServiceBranch || '(not set)',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      });
    }

    return true;
  }

  console.log('[MachineIdMonitor] State is valid - no issues detected');
  return false;
}

